import { DATETIME_FORMAT, DATETIME_FORMAT_24H, DATE_ONLY_FORMAT, STORED_DATE_ONLY_FORMAT, TIME_FORMAT, TIME_FORMAT_24H } from "./constants"
import {
    IconAttachment,
    IconAddModifier,
    IconCalendarWeek,
    IconCheckboxChecked,
    IconCheckboxIndeterminate,
    IconCheckboxUnchecked,
    IconCircle,
    IconCircleFilled,
    IconCostCode,
    IconCraftWorker,
    IconDelay,
    IconDocumentGeneric,
    IconDocumentImage,
    IconDocumentPDF,
    IconDocumentText,
    IconDocumentVideo,
    IconEquipment,
    IconLocation,
    IconMaterials,
    IconMeasurement,
    IconMisc,
    IconRadioButtonSelected,
    IconRadioButtonUnselected,
    IconSafety,
    IconSettings,
    IconSignature,
    IconTime,
    IconTrash,
} from "@rhumbix/rmbx_design_system_web"
import { MinuteInterval, tFormAttachment } from "../new-custom-forms/components/types"
import { EmployeeFormStore, SignaturePadType, TimekeepingEntry, iCurrentUser, tJSONValue, tTransformOption } from "./types"
import { addDays, addWeeks, differenceInHours, differenceInMinutes, format, isValid, max, min, parseISO, set } from "date-fns"
import { iMutableFilterState, tStartOfWeekIndex } from "../filters/types"
import { tContext, tGroupKeyInfo } from "../components/custom-dashboards/types"
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz"

import { LDClient } from "launchdarkly-js-client-sdk"
import { ReactElement } from "react"
// pieces needed to build locale so we can set the start of the week
import formatDistance from "date-fns/locale/en-US/_lib/formatDistance"
import formatLong from "date-fns/locale/en-GB/_lib/formatLong/index.js"
import formatRelative from "date-fns/locale/en-US/_lib/formatRelative/index.js"
import { getFlagEnabled } from "../getFlagValue"
import { iEmployeeWorkShift, tMinimalEmployeeWorkShiftFormSchema } from "../cached-data/types"
import { isEmpty } from "lodash"
import { isNumber } from "./validators"
import localize from "date-fns/locale/en-US/_lib/localize/index.js"
import match from "date-fns/locale/en-US/_lib/match/index.js"
import { RowErrorData, tResourceObject } from "../dashboard-data/types"

type tDateTimeFormatResponse = {
    dateFormat: string
    timeFormat: string
}

type tDatetimeAdjustments = {
    hours?: number | undefined
    minutes?: number | undefined
    seconds?: number | undefined
    milliseconds?: number | undefined
}

/**
 * Compares two unordered arrays for equality. [1,2,3] and [3,2,1] are equivalent in this case.
 *
 * If the two arrays are not equal, return false, otherwise return true.
 */
export const compareUnorderedNumberArrays = (array1: Array<number | string>, array2: Array<number | string>): boolean => {
    if (array1.length !== array2.length) {
        return false
    }

    if (array1.length === 1 && array2.length === 1) {
        return !!(array1[0] === array2[0])
    } else {
        const sortedArray1: Array<number | string> = array1.slice(0).sort()
        const sortedArray2: Array<number | string> = array2.slice(0).sort()

        const resultArray = sortedArray1.filter((item, index) => {
            return item !== sortedArray2[index]
        })

        return !!(resultArray.length === 0)
    }
}

/**
 * Given 2 dates, return the duration as a string HH:MM format
 */
export const formatDurationAsHoursAndMinutes = (endDate: Date, startDate: Date): string => {
    const hours = Math.abs(differenceInHours(endDate, startDate))
    const minutes = Math.abs(differenceInMinutes(endDate, startDate)) % 60
    return `${hours}:${String(minutes).padStart(2, "0")}`
}

/**
 * Given a decimal number of hours, return a string of hours and minutes.
 * @param decimalHours - The decimal number of hours.
 * @returns A string of hours and minutes.
 */
export const convertDecimalHoursToHoursAndMinutes = (decimalHours: number): string => {
    const floorOrCeil = decimalHours > 0 ? Math.floor : Math.ceil
    const hours = Math.abs(floorOrCeil(decimalHours))
    const minutes = Math.abs(Math.round((Math.abs(decimalHours) - hours) * 60))
    if (hours) {
        return `${decimalHours > 0 ? "" : "-"}${hours}h${minutes}m`
    } else if (minutes) {
        return `${decimalHours > 0 ? "" : "-"}${minutes}m`
    } else {
        return "--"
    }
}

/**
 * Converts minutes to hours, rounds the leftover minutes, and then formats to 2 decimal places.
 * This is different behavior than date-fns' minutesToHours(), which only rounds to whole hours.
 * @param minutes
 * @returns value rounded to 2 decimal places
 */
export const minutesToHours = (minutes: number): string => {
    const hours = minutes / 60
    const roundedHours = Math.round((hours + Number.EPSILON) * 100) / 100
    return roundedHours.toFixed(2)
}

/**
 * Given a date object or string, and an optional timezone, returns a date object that can be converted to tz's time
 * If no timezone is given, we use the assumed local timezone of the user
 */
export const convertUtcToTimezone = (dateString: Date | string, tz: string = getAssumedLocalTimezone()): Date => {
    // converts a date/date string into a timezone offset Date
    return utcToZonedTime(dateString, tz)
}

/**
 * Given a date object or string, and an optional timezone, returns a date object that can be converted to UTC time
 * If no timezone is given, we use the assumed local timezone of the user
 * To get the UTC timestamp from the date object you have to use .toISOString()
 */
export const convertTimezoneToUtc = (value: Date | string, tz: string = getAssumedLocalTimezone()): Date => {
    return zonedTimeToUtc(value, tz)
}

/**
 * Round date up to the closest time based on minute interval. If no datetime provided, defaults to now.
 * If a minuteInterval is given, it will round to the closest minute interval
 * If a time is given, it will be rounded up, if no time given will default to a new date/current time
 * @param { MinuteInterval } minuteInterval - Any legit minute interval. Defaults to 15.
 * @param { Date | undefined } datetime - Pass in existing date object to be rounded up. Defaults to new Date().
 * @returns { Date } date object with a value set as close to current time as minuteInterval allows
 */
export const getRoundUpTime = (minuteInterval: MinuteInterval = 15, datetime?: Date): Date => {
    const selectedDateTime = datetime ?? new Date()
    const minutes = selectedDateTime.getMinutes()
    const remainder = minuteInterval - (minutes % minuteInterval)
    // adjust the time to be the existing minutes + the number of min to round up to the proper minute interval
    if (remainder !== minuteInterval) {
        return adjustDatetimeValues(selectedDateTime, { minutes: minutes + remainder })
    }
    return selectedDateTime
}

/**
 * Round a duration to the nearest increment.
 * @param duration - The duration to round.
 * @param increment - The increment to round to.
 * @param roundingIncrement - The rounding cutoff to use.
 * @returns The rounded duration.
 */
export const roundDurationToNearestIncrement = (duration: number, increment: number, roundingIncrement = 0): number =>
    duration % increment >= roundingIncrement ? Math.ceil(duration / increment) * increment : Math.floor(duration / increment) * increment

/**
 * Given a mode string, and a boolean checking for military time, returns an obj with the date and time formats
 * @param mode Specify "date" for date-only, "time" for time-only, or "datetime" for date and time
 * @param is24Hour  Set to true for a 24-hour (military-style) time format
 */
export const getDateTimeFormats = (mode: string, is24Hour: boolean): tDateTimeFormatResponse => {
    let dateFormat = is24Hour ? DATETIME_FORMAT_24H : DATETIME_FORMAT
    const timeFormat = is24Hour ? TIME_FORMAT_24H : TIME_FORMAT
    if (mode == "date") {
        dateFormat = DATE_ONLY_FORMAT
    } else if (mode == "time") {
        dateFormat = timeFormat
    }
    return { dateFormat: dateFormat, timeFormat: timeFormat }
}

/**
 * A easier way to reference the built in Intl functionality for getting user's local timezone
 */
export const getAssumedLocalTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone

/**
 * Given a timezone string, or using the assumed local timezone, return a string with the timezone acronym or offset
 */
export const getTimezoneAcronym = (tz: string = getAssumedLocalTimezone(), overrideDate?: Date | string | null): string => {
    const tzFmt = new Intl.DateTimeFormat("en", {
        timeZoneName: "short",
        timeZone: tz,
    })

    const date = getFlagEnabled("WA-9036-fix-format-date-tz") && overrideDate ? getAsDate(overrideDate) : new Date()
    // if tz has no acronym, show the UTC offset of GMT+4 (https://stackoverflow.com/a/61109337/6710369)
    const currentTimeStr = tzFmt.format(date)
    return currentTimeStr.slice(currentTimeStr.search(/ [A-Z+0-9-]*$/g))
}

/**
 * We have a lot of place where we interchange if we are using date strings or date objects, so using this
 * will ensure that we always are passing a Date object into date-fns methods that expect Date objects
 */
export const getAsDate = (dateValue: string | Date): Date => {
    if (typeof dateValue === "string") {
        dateValue = parseISO(dateValue)
    }
    return dateValue
}

/**
 * Returns true if a given date string is in ISO format
 * @param { string } value - string value of the selected date for a date selector
 * @returns { boolean } - true if date string is in ISO Format, false otherwise
 */
export const isISOFormat = (value: string): boolean => {
    return value ? isValid(parseISO(value)) : false
}

/**
 * Given a date object, hour and minute integers - return a date object with those adjustments
 * ex adjustDatetimeValues(datObj, {"hours": 8, "minutes": 0})
 *
 * Second/millisecond values default to 0 unless they are specifically given
 */
export const adjustDatetimeValues = (date: Date, adjustments: tDatetimeAdjustments): Date => {
    adjustments["seconds"] = adjustments["seconds"] ? adjustments["seconds"] : 0
    adjustments["milliseconds"] = adjustments["milliseconds"] ? adjustments["milliseconds"] : 0
    return set(date, adjustments)
}

/**
 *
 * @param {tStartOfWeekIndex} startOfWeekIndex 0 = Sun / 6 = Sat
 * Given a start of the week index, return a locale object that can be used by react-datepicker
 */
export const getLocaleForDatePicker = (startOfWeekIndex: tStartOfWeekIndex): Record<string, any> => {
    return {
        formatDistance: formatDistance,
        formatLong: formatLong,
        formatRelative: formatRelative,
        localize: localize,
        match: match,
        options: {
            weekStartsOn: startOfWeekIndex,
        },
    }
}

/**
 * @param {string} key - key to find
 * @param {any} obj - object to search
 * @returns any
 * Retrieve a nested key from a deeply nested dictionary. This doesn't handle nested
 * arrays because we're assuming we're not looking for keys from nested lists.
 */
export const recursiveDictKeyLookup = (key: string, obj: Record<string, any>): any => {
    if (!obj || !key) return null
    if (key in obj) {
        return obj[key]
    }
    for (const val of Object.values(obj)) {
        if (val && typeof val === "object") {
            const candidate = recursiveDictKeyLookup(key, val)
            if (candidate) {
                return candidate
            }
        }
    }
    return null
}

/**
 * Serialize a value to JSON with object keys sorted, so two objects with the same keys and values
 * but different insertion order produce an identical string. Array order is left untouched, since
 * it's semantically meaningful. Intended for building stable cache/comparison keys from plain data
 * (e.g. API query params), where plain `JSON.stringify` would be brittle to insertion order.
 * @param value The value to serialize.
 * @returns The sorted-key JSON string.
 */
export const stableStringify = (value: unknown): string => {
    const sortKeys = (input: unknown): unknown => {
        if (Array.isArray(input)) return input.map(sortKeys)
        if (input !== null && typeof input === "object") {
            return Object.keys(input as Record<string, unknown>)
                .sort()
                .reduce((acc, key) => {
                    acc[key] = sortKeys((input as Record<string, unknown>)[key])
                    return acc
                }, {} as Record<string, unknown>)
        }
        return input
    }
    return JSON.stringify(sortKeys(value))
}

/**
 * Add the number of weeks to the given date.
 * @param {Date} date Date.
 * @param {number} numberOfWeeks Number of weeks to add.
 * @param {string} [dateFormat] Output date format.
 * @returns {Date} Calculated date.
 */
export const getDateWithWeeks = (date: Date, numberOfWeeks: number, dateFormat?: string): string => format(addWeeks(date, numberOfWeeks), dateFormat || STORED_DATE_ONLY_FORMAT)

/**
 * Number of weeks to pad a TK summary fetch's start date back by, so the consecutive-days streak
 * calculation has enough prior history to detect a streak spanning into the requested range.
 * Gated behind WA-9712-work-rule-upgrades: the back-end's own padding (see
 * `build_timecard_summary`) always supports the wider window regardless of this flag, so flipping
 * it off only narrows what the front-end asks for, it never breaks a request.
 * @returns {number} A negative number of weeks, for use with `getDateWithWeeks`.
 */
export const getConsecutiveDaysLookbackWeeks = (): number => (getFlagEnabled("WA-9712-work-rule-upgrades") ? -2 : -1)

/**
 * @param {string} value - input string to check for a number
 * @returns string | null
 * Guides the user through typing a proper number into a text input field.
 * They can type a number freely, but they won't be able to input nonsense.
 * Unfortunately, this can still result in something that looks like it *could* become
 * a number, such as "-" or ".", so the return value may not necessarily parse to a number.
 */
export const prepNumberFieldValue = (value: string): string | null => {
    // Wonderful guide to this regex for matching decimals:
    // https://regexland.com/regex-decimal-numbers/
    // Also matches an in-progress decimal or negative number, e.g. "-" or "."
    const decimalRegex = /[+-]?([0-9]+\.?[0-9]*|\.[0-9]+)|[+-]|\./
    const match = value.match(decimalRegex)
    if (match == null) return null
    return match[0]
}

export const fahrenheitToCelsius = (tempF: number, decimalPlaces: number): string => {
    return (((tempF - 32) * 5) / 9).toFixed(decimalPlaces)
}

/**
 * Given an angle, return the cardinal (compass) direction acronym for that angle.
 * @param windAngle - number between 0-360
 * @returns cardinal direction acronym string
 */
export const angleToCardinalDirection = (windAngle: number): string => {
    if (!isNumber(windAngle)) return ""
    const directions = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"]
    return directions[Math.round(windAngle / 22.5) % 16]
}

export const convertDotPathToJsonPointer = (dotPath: string): string => {
    const regex = /\./g
    return `/${dotPath.replace(regex, "/")}`
}

export const convertJsonPointerToDotPath = (jsonPath: string): string => {
    const regex = /\//g
    if (!jsonPath) return ""
    // trim off the initial /
    if (jsonPath.startsWith("/")) {
        jsonPath = jsonPath.substring(1)
    }
    return jsonPath.replace(regex, ".")
}

/**
 * @param {object} response - response object from a 400-level API error response
 * @param {string} defaultMessage - default error message to use if one can't be parsed from the response
 * @returns string
 * Attempts to parse out an error message from the myriad of error responses the app might encounter.
 */
// TODO: Update as new error response structures are discovered.
type ErrorData = {
    detail?: string
    errors?: {
        non_field_errors?: string[] | string
        detail?: string
    } & Record<string, string>
}
type ErrorResponse = { data: ErrorData }
type HttpErrorResponse = { response: ErrorResponse }
export const getMessageFromAPIErrorResponse = (response: HttpErrorResponse | ErrorResponse | ErrorData, defaultMessage: string): string => {
    let message = defaultMessage
    // Check for an "errors" key in the error response
    // OK if |errors| ends up undefined.
    let errorData: ErrorData
    if ("response" in response) {
        // Dealing with a full-blown HTTP Response object
        errorData = response.response.data
    } else if ("data" in response) {
        // Dealing with an unwrapped Response object
        errorData = response.data
    } else if ("detail" in response || "errors" in response) {
        // Response data was already passed in
        errorData = response
    } else {
        // Who knows what we've got on our hands
        return defaultMessage
    }

    // There might just be a top-level "detail" key
    if (errorData.detail) {
        return errorData.detail
    }

    // Usually there's an "errors" key to comb through
    const { errors } = errorData
    if (errors?.non_field_errors) {
        const non_field_errors = errors.non_field_errors
        if (Array.isArray(non_field_errors)) {
            message = non_field_errors[0]
        } else {
            message = non_field_errors
        }
    } else if (errors?.detail) {
        message = errors.detail
    } else {
        // Take the first field we find
        for (const key in errors) {
            message = errors[key]
            break
        }
    }

    return message || defaultMessage
}

// Keys that don't hold user-facing error text: "exception" is debug-only traceback data, and "row" is
// a fallback summary string that write.ts derives from the other fields (falling back to a generic
// message, e.g. "Invalid Input", when it can't find a specific one) - we prefer the specific messages
// found elsewhere in the object and only fall back to "row" if nothing else turns up.
const NON_DISPLAYABLE_ROW_ERROR_KEYS = ["exception", "row"]

const collectErrorStrings = (value: unknown, messages: Set<string>): void => {
    if (Array.isArray(value)) {
        value.forEach(item => collectErrorStrings(item, messages))
    } else if (value && typeof value === "object") {
        Object.entries(value).forEach(([key, nested]) => {
            if (!NON_DISPLAYABLE_ROW_ERROR_KEYS.includes(key)) collectErrorStrings(nested, messages)
        })
    } else if (typeof value === "string" && value) {
        messages.add(value)
    }
}

/**
 * Flatten a RowErrorData object (as returned by updateSSRMDataRow/updateSourceDataRow) into a
 * single, human-readable string, for display somewhere that can't render per-field errors (e.g. a toast).
 * @param errors The (possibly deeply-nested) error data attached to a dashboard row.
 * @returns The distinct error messages found (joined by newlines), falling back to `errors.row`
 * if no specific message could be found, or an empty string if there's nothing to show.
 */
export const getRowErrorMessage = (errors?: RowErrorData): string => {
    if (!errors) return ""
    const messages = new Set<string>()
    collectErrorStrings(errors, messages)
    return [...messages].join("\n") || errors.row || ""
}

export const featureFlagClientIdentifyUser = (user: iCurrentUser, client: LDClient): void => {
    // Monthly Active Users (MAUs) are tracked by key and affect billing.
    // We don't want to track test users because that would run up the MAU total.
    // Instead, use a static MAU key for all test users so they collectively count as just one MAU.
    const key =
        // BUILD_ENV will be "test" when the bundle is built specifically for UI testing.
        // NODE_ENV will be automatically set to "test" by Jest for unit testing.
        process.env.BUILD_ENV === "test" || process.env.NODE_ENV === "test" ? "authenticated-test-user" : user.employee_id.toString()
    const loggedInUserContext = {
        kind: "user",
        key,
        anonymous: false,
        company_id: user.company_id,
        company_name: user.company,
        company_role: user.company_role,
        domain: window.ld_domain,
        email: user.email,
        is_staff: user.is_staff,
        platform: "web",
        user_role: user.user_role,
        _meta: {
            privateAttributes: ["email"],
        },
    }
    client.identify(loggedInUserContext)
}

/**
 * Given the relatedTransform and relatedSchemas from the context object, can the selected
 * forms be bundled.
 * @param {number | null} relatedTransform - transform number
 * @param {array | null} relatedSchemas - list of schemas
 * @returns boolean - returns true if forms can be bundled
 */
export const isBundleEnabled = (relatedTransform: number | null, relatedSchemas: any[] | null): boolean => {
    return !!relatedTransform && !!relatedSchemas?.length && relatedSchemas?.every(schema => schema.schema_type === "BUNDLE")
}

/**
 * Take a string and convert it into a clean file name for file download naming
 * ex. "T&M Tracking" -> "tm_tracking"
 * @param initialValue
 * @returns
 */
export const convertToFilename = (initialValue: string): string => {
    // if nothing is given, return a generic filename
    if (!initialValue) return "rhumbix_download"
    // remove anything that isn't a letter or digit, space, underscore, /
    let cleanFilename = initialValue.replace(/[^a-zA-Z0-9_ /-]/gi, "")
    // remove any trailing or leading spaces
    cleanFilename = cleanFilename.trim()
    // swap 1 or more spaces for underscores
    cleanFilename = cleanFilename.replace(/[ /-]+/gi, "_")
    // set all char to be lowercase
    return cleanFilename.toLowerCase()
}

/**
 * Given a list of transforms and a schema id, find all Copy-To transforms whose from_schemas list
 * contains the schema id. The to_schema is intentionally not matched so that cross-schema Copy-To
 * transforms (where the destination schema differs from the source) are included.
 * @param transforms List of transforms
 * @param schemaId ID of schema to match
 * @returns List of valid transforms where schema id is present in from_schemas
 */
export const filterCopyToTransformsBySchemaId = (transforms: tTransformOption[], schemaId: number): tTransformOption[] => {
    return filterTransformsBySchemaId(transforms, schemaId, true)
}

/**
 * Given a list of transforms and a schema id, find all schemas that match the from schema and/or the to schema
 * Copy-To transforms would match both the to and from schema
 * For Bundles - the from schema would match the form schema id, and the to schema would be the bundle schema id
 * @param transforms List of transforms
 * @param schemaId ID of schema to match
 * @param matchFromSchema If true, will filter transforms based on if schema id is in from_schemas list
 * @param matchToSchema If true, will filter transforms based on if the schema id matches the to schema
 * @returns List of valid transforms for the provided schema id and match settings.
 */
export const filterTransformsBySchemaId = (transforms: tTransformOption[], schemaId: number, matchFromSchema = false, matchToSchema = false): tTransformOption[] => {
    if (!transforms?.length || !schemaId || (!matchFromSchema && !matchToSchema)) return []
    let validTransforms: tTransformOption[] = transforms
    if (matchFromSchema) {
        validTransforms = validTransforms.filter(transform => transform.from_schemas?.includes(schemaId))
    }
    if (matchToSchema) {
        validTransforms = validTransforms.filter(transform => transform.to_schema === schemaId)
    }
    return validTransforms
}

/**
 * Gets all transforms where the schema(s) that are related to the selected form(s) are in the from_schemas list
 * Returns all valid transforms for the selected row(s)
 * @param context - list view context
 * @param bundleTransforms List of transforms
 * @returns Set of all valid transforms or an empty set if no valid bundle transforms available
 */
export const getBundleTransforms = (context: tContext, bundleTransforms: tTransformOption[]): Set<tTransformOption | null> => {
    const validTransforms: Set<tTransformOption | null> = new Set()
    context.selectedRows.forEach(store => {
        const bundleTransformForSchema = filterTransformsBySchemaId(
            bundleTransforms,
            store?.schema,
            true // matchFromSchema
        )
        // we need to account for schemas that don't have bundling set up, so add a null if no valid bundles
        if (!bundleTransformForSchema?.length) validTransforms.add(null)
        else {
            // if there are more than one matching bundle transforms for a given schema, use the newest one
            bundleTransformForSchema.sort((a, b) => (a.created_on < b.created_on ? -1 : a.created_on === b.created_on ? 0 : 1))
            validTransforms.add(bundleTransformForSchema[0])
        }
    })
    // if we only have nulls in the set of transforms - return empty set
    if (validTransforms.size === 1 && validTransforms.values().next().value === null) return new Set()
    return validTransforms
}

export type IconName =
    | "attachment"
    | "checkboxChecked"
    | "checkboxIndeterminate"
    | "checkboxUnchecked"
    | "circleEmpty"
    | "circleFilled"
    | "cost_code"
    | "date"
    | "default"
    | "delay"
    | "delete"
    | "document"
    | "employee"
    | "equipment"
    | "file"
    | "fileIcon"
    | "labor"
    | "location"
    | "material"
    | "materials"
    | "measurement"
    | "misc"
    | "modifier"
    | "pdf"
    | "pdfIcon"
    | "project"
    | "radioSelected"
    | "radioUnselected"
    | "safety"
    | "signature"
    | "time"
    | "trash"
    | "text"
    | "textIcon"
    | "video"
    | "videoIcon"

/**
 * Given an icon name, return the Icon from the Design System as React Element - defaults to misc if not found
 * @param { IconName } name A valid icon name - see IconName type for options
 * @param { Record<string, any> } styles Optional. Any specific styles you want applied to the icon
 * @returns SVG Icon as React Element
 */
export const getStyledIcon = (name: IconName | undefined, styles: Record<string, string>): ReactElement => {
    switch (name) {
        case "attachment":
            return new IconAttachment(styles)
        case "checkboxChecked":
            return new IconCheckboxChecked(styles)
        case "checkboxIndeterminate":
            return new IconCheckboxIndeterminate(styles)
        case "checkboxUnchecked":
            return new IconCheckboxUnchecked(styles)
        case "circleEmpty":
            return IconCircle(styles)
        case "circleFilled":
            return IconCircleFilled(styles)
        case "cost_code":
            return new IconCostCode(styles)
        case "date":
            return new IconCalendarWeek(styles)
        case "delay":
            return new IconDelay(styles)
        case "delete":
        case "trash":
            return new IconTrash(styles)
        case "equipment":
            return new IconEquipment(styles)
        case "labor":
        case "employee":
            return new IconCraftWorker(styles)
        case "material":
        case "materials":
            return new IconMaterials(styles)
        case "measurement":
            return new IconMeasurement(styles)
        case "misc":
            return new IconMisc(styles)
        case "modifier":
            return new IconAddModifier(styles)
        case "project":
            return new IconSettings(styles)
        case "radioSelected":
            return new IconRadioButtonSelected(styles)
        case "radioUnselected":
            return new IconRadioButtonUnselected(styles)
        case "safety":
            return new IconSafety(styles)
        case "signature":
            return new IconSignature(styles)
        case "time":
            return new IconTime(styles)
        case "pdfIcon":
        case "pdf":
            return new IconDocumentPDF(styles)
        case "textIcon":
        case "document":
        case "text":
            return new IconDocumentText(styles)
        case "videoIcon":
        case "video":
            return new IconDocumentVideo(styles)
        case "fileIcon":
        case "file":
            return new IconDocumentImage(styles)
        case "location":
            return new IconLocation(styles)
        default:
            return new IconMisc(styles)
    }
}

export const getFileIconOrImage = (attachment: tFormAttachment, iconStyle: Record<string, any>, className = "", isThumbnail = false): HTMLImageElement | JSX.Element => {
    let thumb = <IconDocumentGeneric style={{ ...iconStyle }} />
    if (attachment.type === "application/pdf") {
        thumb = <IconDocumentPDF style={{ ...iconStyle }} />
    } else if (attachment.type?.startsWith("image/")) {
        thumb = <img src={isThumbnail ? attachment.thumbnail_url ?? attachment.url : attachment.url} alt="Thumbnail" className={className} />
    } else if (attachment.type?.startsWith("video/")) {
        thumb = <IconDocumentVideo style={{ ...iconStyle }} />
    }
    return thumb
}

/**
 * Given a url string, will return an object where the query param is the key and the value is, well, the value
 * @param {string} url
 * @returns {Object} Object with breakdown of the queryparams in the url
 */
export const parseQueryString = (url: string): Record<string, any> => {
    // pull out querystring params to pass on to action and subsequent api call
    const queryParams = url.split("?")[1] || ""
    const queryObj: Record<string, any> = {}
    for (const group of queryParams.split("&")) {
        if (group) {
            const keyValue = group.split("=")
            queryObj[keyValue[0]] = keyValue[1]
        }
    }
    return queryObj
}

/**
 * Given a list of objects with at least one field that holds a date or date string, will return an object
 * with minDate and maxDate keys with the min/max dates from the list
 * @param { Record<string, any> }objs List of objects
 * @param { string } dateKey Key with the targeted date value
 * @returns { Record <"minDate" | "maxDate", Date>} min and max date values as Date objects
 */
export const getMinMaxDates = (objs: Record<string, any>[], dateKey: string): Record<"minDate" | "maxDate", Date> => {
    const allDates: Date[] = []
    objs.forEach(item => {
        // if the item has a date, and it's valid, get it as a date object
        if (item?.[dateKey]) {
            try {
                const itemDate = getAsDate(item?.[dateKey])
                allDates.push(itemDate)
            } catch {
                // if we can't get the value as a date, skip it
                return
            }
        }
    })
    const maxDate = max(allDates)
    const minDate = min(allDates)
    return { maxDate, minDate }
}

/**
 * Given a number of minutes, the value will be converted into a hh:mm timestamp
 * @param {number} minutes
 * @returns {string} minute value converting into "hh:mm"
 */
export const convertMintoTimestamp = (minutes: number): string => {
    //return string of hours:minutes from a number of minutes
    const hours = Math.floor(minutes / 60)
    const min: number = Math.floor(minutes - hours * 60)
    //return HH:MM format
    return `${hours}:${String(min).padStart(2, "0")}`
}

type timeType = "totalTime" | "stTime" | "otTime" | "dtTime"

/**
 * Given a list of employee workshifts, return an object with ST/OT/DT/Total times in "hh:mm" timestamps
 * @param ewss list of employeeworkshifts to total
 * @returns {Record<string, string>} Object with breakdowns of time
 */
export const getEWSTimeBreakdown = (ewss: iEmployeeWorkShift[]): Record<timeType, string> => {
    let stMin = 0
    let otMin = 0
    let dtMin = 0
    // go through all employee work shifts and their respective work components to get all time in minutes
    ewss.forEach(ews => {
        if (ews.work_components) {
            ews.work_components.forEach(wc => {
                if (wc.adjusted_minutes_st) stMin += wc.adjusted_minutes_st
                if (wc.adjusted_minutes_ot) otMin += wc.adjusted_minutes_ot
                if (wc.adjusted_minutes_dt) dtMin += wc.adjusted_minutes_dt
            })
        }
    })
    const totalTime = convertMintoTimestamp(stMin + otMin + dtMin)
    const stTime = convertMintoTimestamp(stMin)
    const otTime = convertMintoTimestamp(otMin)
    const dtTime = convertMintoTimestamp(dtMin)
    return {
        totalTime,
        stTime,
        otTime,
        dtTime,
    }
}

export type TKCostCodeModifier = {
    code: string
    is_active: boolean
    name: string
    type_name: string
    type_order: number
}

export type TKEquipmentModifier = TKCostCodeModifier & {
    caltrans_id: string
    category: string
    description: string
    equipment_custom_fields: Record<string, any> | null
    equipment_id: string
    id: number
    status: string
}

export type TKHours = {
    st?: number
    ot?: number
    dt?: number
}

type TKModifierSet = Record<string, TKCostCodeModifier | TKEquipmentModifier | number>

export type ModifierHours = {
    [key: string]: TKHours
}

// because we return the full modifier with the TK Signature EWS, we need to adjust the type a bit
export type TKSignatureEWS = Omit<iEmployeeWorkShift, "work_components" | "shift_extras"> & {
    work_components: (TimekeepingEntry & {
        modifier_active: TKModifierSet
    })[]
    shift_extras: (EmployeeFormStore & {
        form_schema: tMinimalEmployeeWorkShiftFormSchema
        project_job_number: string
    })[]
}

// a unique delimiter to split the cost code description from the modifier description
export const CC_MODIFIER_DELIMITER = " |-SPLIT-HERE-| "

/**
 * Given a list of employee work shifts, return a summary of any relevant modifiers
 * Modifiers are stacked together on WCs. We will concat modifiers into a single description if a WC has multiple
 * Examples:
 *   "Morning (0600-1400) • Northwest (NW1234}"
 *   "Overnight (2200-0600)"
 *   "Overnight (2200-0600) • Northwest (NW1234)"
 *
 * If no cost codes are provided, modifiers will be aggregated by description, and cost codes will be ignored
 *
 * Optionally, pass in a cost codes mapping and the resulting keys will include the cost code so values
 * will be aggregated by cost code and modifier description.
 * Modifiers and cost code ids will be separated by a unique delimiter CC_MODIFIER_DELIMITER.
 * This ensures that we get the proper unique modifier keys for the different cost codes. We split on the delimiter
 * to get the modifier description for display without having to change the structure of the returned object
 * Examples:
 *   Morning (0600-1400) • Northwest (NW1234)"|-SPLIT-HERE-|12345"
 *   "Overnight (2200-0600)|-SPLIT-HERE-|12356"
 *   "Overnight (2200-0600) • Northwest (NW1234)|-SPLIT-HERE-|13476"
 *
 * @param employeeWorkShifts list of employee work shifts
 * @param byCostCode if true, the modifier keys will include the cost code id
 * @returns summary of the modifiers
 */
export const getModifiersSummary = (employeeWorkShifts: { work_components: TimekeepingEntry[] }[], byCostCode = false): ModifierHours => {
    const modifiers: ModifierHours = {}

    employeeWorkShifts.forEach(ews => {
        const workComponents = ews?.work_components ?? []
        workComponents.forEach(wc => {
            const modifierActive = wc?.modifier_active
            if (!modifierActive) return
            let description = ""
            const modifierValues: string[] = []
            Object.values(modifierActive).forEach(value => {
                if (typeof value === "number") return
                const modifier = value as TKCostCodeModifier | TKEquipmentModifier
                modifierValues.push(
                    modifier?.type_name?.toLowerCase() === "equipment"
                        ? `${(modifier as TKEquipmentModifier)?.description ?? ""} (${(modifier as TKEquipmentModifier)?.equipment_id})`
                        : `${modifier?.name ?? ""} (${modifier?.code})`
                )
            })
            if (modifierValues?.length) {
                const modifierDescription = modifierValues.join(" • ")
                // if we aren't grouping by cost code, just use the modifier description, and they will be grouped
                // on tk sigs, we aggregate modifiers by description, on tk pdf, we break them out by cost code
                // only set the combined description if we have both cost code and modifier description
                description = byCostCode ? `${modifierDescription}${CC_MODIFIER_DELIMITER}${wc.cost_code}` : modifierDescription
            }
            if (!description) return
            // add any values to any existing values with the same modifier keys
            modifiers[description] = {
                st: (modifiers?.[description]?.st ?? 0) + (wc?.adjusted_minutes_st ?? 0),
                ot: (modifiers?.[description]?.ot ?? 0) + (wc?.adjusted_minutes_ot ?? 0),
                dt: (modifiers?.[description]?.dt ?? 0) + (wc?.adjusted_minutes_dt ?? 0),
            }
        })
    })

    return modifiers
}

// order of "granularity" for signature pad types
const padTypes: Record<SignaturePadType, number> = {
    DRAW: 1,
    TYPE: 2,
    HYBRID: 3,
}

/**
 * Given a list of employee workshifts, check work components for project level settings for signature
 * If we are signing for multiple projects, we defer to the most granular setting. Given a mix of booleans,
 * we will defer to the truthy setting.
 * @param ewss list of employeeworkshifts to total
 * @returns {Record<string, string>} Object with signature settings for signature screen
 */
export const getSigSettingsFromEws = (ewss: iEmployeeWorkShift[]): Record<string, any> => {
    // start with the broadest possible settings
    const sigSettings = {
        signaturePeriod: "WEEKLY",
        signaturePadType: "HYBRID" as SignaturePadType,
        promptEodQuestions: false,
        displayStartStop: false,
        displayShiftExtras: false,
        displayModifiers: false,
    }
    ewss.forEach(ews => {
        if (ews.work_components) {
            ews.work_components.forEach(wc => {
                if (wc.signature_period === "DAILY") sigSettings.signaturePeriod = "DAILY"
                if (wc.signature_pad_type && padTypes[wc.signature_pad_type] < padTypes[sigSettings.signaturePadType]) sigSettings.signaturePadType = wc.signature_pad_type
                if (wc.prompt_eod_questions) sigSettings.promptEodQuestions = true
                if (wc.display_start_stop) sigSettings.displayStartStop = true
                if (wc.display_shift_extras) sigSettings.displayShiftExtras = true
                if (wc.display_modifiers) sigSettings.displayModifiers = true
            })
        }
        if (ews.shift_extras) {
            ews.shift_extras.forEach((se: EmployeeFormStore) => {
                if (se.signature_period === "DAILY") sigSettings.signaturePeriod = "DAILY"
                const padType = se.signature_pad_type as SignaturePadType
                if (padType && padTypes[padType] < padTypes[sigSettings.signaturePadType]) sigSettings.signaturePadType = padType
                if (se.display_start_stop) sigSettings.displayStartStop = true
                if (se.prompt_eod_questions) sigSettings.promptEodQuestions = true
                if (se.display_shift_extras) sigSettings.displayShiftExtras = true
                if (se.display_modifiers) sigSettings.displayModifiers = true
            })
        }
    })
    return sigSettings
}

/**
 * Given a boolean, return Yes or No string based on the value
 */
export const convertBoolToYesNo = (value: boolean): "Yes" | "No" => {
    return value ? "Yes" : "No"
}

/**
 * Used to get the named key from the cookie
 * @param name value you want to extract from the cookie
 * @returns
 */
export const getCookie = (name = "csrftoken"): string | null => {
    let cookieValue = null
    if (document.cookie && document.cookie !== "") {
        const cookies = document.cookie.split(";")
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim()
            if (cookie.substring(0, name.length + 1) === name + "=") {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1))
                break
            }
        }
    }
    return cookieValue
}

/**
 * Sets a cookie
 * @param name The name of the cookie you want to set
 * @param value The value of the cookie you want to set
 * @param days The number of days you want the cookie to live.
 *             Not providing this value will create a session cookie
 */
export const setCookie = (name: string, value: string, path = "/", days: number | undefined): void => {
    let expires = ""
    if (days) {
        const date = new Date()
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
        expires = "; expires=" + date.toUTCString()
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=" + path
}

/**
 * Deletes a cookie
 * @param name Cookie name you want to delete
 */
export const deleteCookie = (name: string, path = "/"): void => {
    if (getCookie(name)) {
        document.cookie = name + "=" + (path ? ";path=" + path : "") + ";expires=Thu, 01 Jan 1970 00:00:01 GMT"
    }
}

/**
 * Get a boolean value from a cookie. It only stores strings, so this does a little extra work to coerce the value
 * @param name Name of the cookie
 * @param defaultValue default value if the cookie doesn't exist
 */
export const getBooleanCookie = (name: string, defaultValue = false): boolean => (getCookie(name) ? getCookie(name) === "true" : defaultValue)

/**
 * Get an integer value from a cookie. It only stores strings, so this does a little extra work to coerce the value
 * @param name Name of the cookie
 * @param defaultValue default value if the cookie doesn't exist
 */
export const getIntCookie = (name: string, defaultValue = 0): number => {
    const cookieValue = getCookie(name)
    return cookieValue ? parseInt(cookieValue) : defaultValue
}

/**
 * Set the value of a cell's data. Ideally this would live in an ag-grid-ts-util type file,
 * but that currently causes a circular dependency
 * @param rowData The data for the row
 * @param colDef The column definition
 * @param newValue The new value to set it to
 * @param lockedColumns {tGroupKeyInfo[]} All of the locked columns
 */
export const setCellData = (rowData: tResourceObject, colDef: Record<string, any>, newValue: tJSONValue, lockedColumns?: tGroupKeyInfo[]): void => {
    const valueSetter = colDef.valueSetter
    if (valueSetter) {
        valueSetter({
            data: rowData,
            colDef: colDef,
            newValue: newValue,
            lockedColumns,
        })
    } else {
        rowData[colDef.field] = newValue
    }
}

/**
 * Given a url and a filename, download an image
 * @param url
 * @param filename
 */
export const downloadImage = (url: string, filename?: string): void => {
    const link = document.createElement("a")
    link.href = url
    if (filename) link.download = filename
    link.click()
}

/**
 * Given a string, return the string with every word capitalized. All other letters will be lowercased.
 * All instances of whitespace will be replaced with a single space.
 * @param text String to be updated with capital letters
 * @returns String with every word capitalized
 */
export const capitalizeFirstLetters = (text: string): string => {
    if (!text) return ""
    const words = text.trim().split(/\s+/)
    return words.map((word: string) => word.charAt(0).toUpperCase() + word.toLowerCase().slice(1)).join(" ")
}

/**
 * Escapes HTML-significant characters in a string so it can be safely interpolated into markup
 * that will be rendered via `dangerouslySetInnerHTML`, without allowing injected tags/attributes
 * to execute. Only escapes text content -- do not use this on strings that are themselves meant
 * to contain trusted markup (e.g. `<br>`/`<strong>` tags you constructed yourself).
 * @param value Untrusted text to escape
 * @returns The same text with `&`, `<`, `>`, `"`, and `'` replaced by their HTML entities
 */
export const escapeHtml = (value: string): string => value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;")

/**
 * Format a SNAKE_CASE enum value as a title-cased label (e.g. "DAILY" -> "Daily",
 * "FOREMAN_ONLY" -> "Foreman Only").
 * @param value Raw enum value, or undefined
 * @returns Title-cased label, or "" if value is empty
 */
export const formatEnumLabel = (value: string | undefined): string => {
    if (!value) return ""
    return capitalizeFirstLetters(value.replace(/_/g, " "))
}

export const getSelectedRowStatuses = (context: tContext): string[] => {
    const allStatusesForRows = context.selectedRows.map((row: { schema: number; status: string; deleted_on: string }) => {
        let statusTransitions = [] as string[]
        if (context.referenceableData != undefined && context.referenceableData.companyFormSchemas && !row.deleted_on) {
            // we don't allow action on deleted rows
            const schema = context.referenceableData.companyFormSchemas[row.schema]
            if (schema != undefined) {
                statusTransitions = schema.status_transitions[row.status] || []
            }
        }
        return statusTransitions
    })
    // An array of statuses that *every* selected row could be transitioned to
    const firstRowStatuses = allStatusesForRows.shift() || []

    return firstRowStatuses.filter(rowStatus => {
        return allStatusesForRows.every(statusList => statusList.indexOf(rowStatus) !== -1)
    })
}

/**
 * Given a filter state, return a list of projectIds. If no projects filtered, return empty list.
 * @param filters - filter state
 * @returns list of any selected project ids
 */
export const getFilteredProjects = (filters: iMutableFilterState): number[] => {
    if (!filters?.projectId || !getFlagEnabled("WA-7130-disable-shift-extra-add-row")) return []
    return Array.isArray(filters.projectId) ? filters.projectId : [filters.projectId]
}

/**
 * Given a URL, returns all query params in object form.
 * Useful for requesting additional pages of data using a 'next' URL by breaking up the 'next' URL
 * into a base route and a params object.
 *
 * e.g. https://platform.rhumbix.com/api/v4/timekeeping_entries/?group_id=1143&group_id=1145&group_id=1146&page=2
 * returns => {
 *   group_id: [1143, 1145],
 *   page: 2
 * }
 *
 * @param url
 * @returns an object with the URL params as keys and values
 */
export const convertURLParamsToObject = (url: string): Record<string, string | string[]> => {
    const urlParams = new URL(url).searchParams
    // @ts-ignore: URLSearchParams.entries() exists, current TS is out-of-date.
    return [...urlParams.entries()].reduce((paramsObj, [key, value]) => {
        if (paramsObj[key]) {
            if (Array.isArray(paramsObj[key])) {
                // Key already exists with multiple values. Append the new value.
                // @ts-ignore: TS is complaining that this isn't necessarily an array, but we checked!
                paramsObj[key].push(value)
            } else {
                // Key already exists but only has a single value.
                // Convert it to an array of multiple values.
                // @ts-ignore: TS is complaining about potentially nesting a string[] within another string[].
                paramsObj[key] = [paramsObj[key], value]
            }
        } else {
            // Key doesn't exist yet, associate it with a single value.
            paramsObj[key] = value
        }
        return paramsObj
    }, {} as { [key: string]: string | string[] })
}

/**
 * Given a string - replace spaces with separator, removes any non-alpha/non-number char except for /,
 * and converts string to lowercase with no spaces. Spaces are replaced with the separator.
 * @param label Value to convert into slug. If undefined, returns empty string
 * @param separator: Optional - string char to replace spaces. Default is "-"
 * @returns
 */
export const slugify = (label?: string, separator = "-"): string => {
    if (!label) return ""
    return label
        .toLowerCase()
        .replace(/[^a-z0-9 /]/gi, "") // remove any non-alpha/non-digit chars except for spaces and forward slashes
        .replace(/[ -]+/gi, separator) // replace one or more spaces with the separator
}

/**
 * Convert a string to a valid field name (snake_case).
 * Mirrors the Python clean_field_name function from capn/field_forms/utilities.py
 *
 * @example
 * toSnakeCase("Site Supervisor") // "site_supervisor"
 * toSnakeCase("dateOfBirth") // "date_of_birth"
 * toSnakeCase("PPE Check") // "ppe_check"
 * toSnakeCase("Item #") // "item_num"
 * toSnakeCase("Cost/Rate") // "cost_rate"
 */
export function toSnakeCase(str: string): string {
    if (!str) return str

    let cleanName = str
        // Insert underscore before uppercase letters (for camelCase - TypeScript addition)
        .replace(/([a-z])([A-Z])/g, "$1_$2")
        // Replace # with "Num" (matches Python clean_field_name)
        .replace(/#/g, "Num")
        // Remove anything that isn't a letter, digit, space, underscore, slash, or hyphen
        .replace(/[^a-zA-Z0-9_ /-]/g, "")
        // Trim leading/trailing whitespace
        .trim()

    // Handle edge case where string was all special characters
    if (cleanName.length === 0) {
        if (str.includes("$")) {
            cleanName = "Amount"
        } else {
            return "field"
        }
    }

    return (
        cleanName
            // Replace spaces, slashes, and hyphens with underscores
            .replace(/[ /-]+/g, "_")
            // Convert to lowercase
            .toLowerCase()
            // Remove leading/trailing underscores
            .replace(/^_+|_+$/g, "")
            // Collapse multiple underscores
            .replace(/_+/g, "_")
    )
}

export const isDateTheNextDay = (srcDate: Date, targetDate: Date): boolean => {
    const nextDay = addDays(srcDate, 1)
    return nextDay.getFullYear() === targetDate.getFullYear() && nextDay.getMonth() === targetDate.getMonth() && nextDay.getDate() === targetDate.getDate()
}

export const getDisallowedRoles = (user: iCurrentUser): string[] => (user.user_role === "PM" ? ["ADMIN", "PAYROLL_ADMIN"] : user.user_role === "PAYROLL_ADMIN" ? ["ADMIN"] : [])

/**
 * Determine if a value is empty. It could be stringified JSON, so we handle that possibility
 * @param value
 */
export const isValueEmpty = (value: string | number | boolean | object | undefined | Array<any>): boolean => {
    let val
    try {
        val = typeof value === "string" ? JSON.parse(value) : value
    } catch {
        val = value
    }
    return (typeof val === "boolean" && val) || !isEmpty(val)
}

/**
 * Retrieves the geolocation of the user's browser
 * @returns A Promise that resolves to the user's geolocation or undefined if unable to get location
 */
export const getBrowserLocation = async (timeoutMs = 1500): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
        if (navigator.geolocation) {
            const options: PositionOptions = {
                enableHighAccuracy: false, // Faster, less battery intensive
                maximumAge: 0,
                timeout: timeoutMs,
            }
            navigator.geolocation.getCurrentPosition(
                position => {
                    resolve(position)
                },
                error => {
                    console.error("Error getting location:", error?.message)
                    reject(error)
                },
                options
            )
        } else {
            reject(new Error("Geolocation not available"))
        }
    })
}

// Simple location caching to reduce redundant geolocation requests
// Serializable shape for GeolocationPosition
interface SerializablePosition {
    coords: {
        latitude: number
        longitude: number
        accuracy: number
    }
    timestamp: number
}

interface LocationCache {
    position: SerializablePosition
    timestamp: number
}

const LOCATION_CACHE_KEY = "geolocation_cache"
// keep the cache duration short to avoid stale data, but long enough to avoid constant requests
const LOCATION_CACHE_DURATION = 1 * 60 * 1000 // 1 minute

// Helper to serialize GeolocationPosition to a plain object
const serializePosition = (pos: GeolocationPosition): SerializablePosition => {
    return {
        coords: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
        },
        timestamp: pos.timestamp,
    }
}

// Helper to deserialize object back to GeolocationPosition-like shape
const deserializePosition = (obj: any): GeolocationPosition | null => {
    // Validate required numeric fields
    if (!obj?.coords || typeof obj.coords.latitude !== "number" || typeof obj.coords.longitude !== "number" || typeof obj.coords.accuracy !== "number" || typeof obj.timestamp !== "number") {
        return null
    }
    // Return a GeolocationPosition-like object
    return {
        coords: {
            latitude: obj.coords.latitude,
            longitude: obj.coords.longitude,
            accuracy: obj.coords.accuracy,
        },
        timestamp: obj.timestamp,
    } as GeolocationPosition
}

const getLocationFromSessionStorage = (): LocationCache | null => {
    try {
        const cached = sessionStorage.getItem(LOCATION_CACHE_KEY)
        if (!cached) return null

        const parsed = JSON.parse(cached)
        if (!parsed || !parsed.position || typeof parsed.timestamp !== "number") {
            return null
        }

        // Validate the position data structure
        const validatedPosition = deserializePosition(parsed.position)
        if (!validatedPosition) {
            // Invalid cached data, clear it and return null
            sessionStorage.removeItem(LOCATION_CACHE_KEY)
            return null
        }

        return parsed as LocationCache
    } catch {
        // If session storage is not available or data is corrupted, return null
        return null
    }
}

const setLocationInSessionStorage = (cache: LocationCache): void => {
    try {
        sessionStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(cache))
    } catch {
        console.warn("Failed to set location cache")
    }
}

const isLocationCacheValid = (cache: LocationCache): boolean => {
    return Date.now() - cache.timestamp < LOCATION_CACHE_DURATION
}

/**
 * Aliasing getLocation to make it explcit when we are prompting for location, and when we aren't
 * Prevents someone from missing the setting and not getting the location permissions prompted properly.
 */
export const promptForLocation = async (): Promise<GeolocationPosition | undefined> => {
    const showPrompt = true
    return await getLocation(showPrompt)
}

/**
 * This will check if the user has granted permission to use their location
 * If they have not set a preference and showPrompt is true, this will trigger a prompt to ask for permission
 * We will return undefined if the user has not granted permission
 * @param showPrompt - if true, prompt user for location perms. Default=false, only return location if perms granted
 * @returns GeolocationPosition | undefined - the user's location or undefined if they haven't granted permission
 */
export const getLocation = async (showPrompt = false, timeoutMs = 1500): Promise<GeolocationPosition | undefined> => {
    // Try to check permissions, but handle cases where it's unavailable
    let permissionState: PermissionState | undefined
    let permissionsAPIAvailable = true
    try {
        const result = await navigator.permissions.query({ name: "geolocation" })
        permissionState = result.state
    } catch {
        // navigator.permissions may be undefined or throw in some browsers
        permissionsAPIAvailable = false
        // When permissions API is unavailable, we can still return cached data if valid
        // or attempt to get location if showPrompt is true
    }

    // Return cached location if valid and permissions allow it
    const cachedLocationData = getLocationFromSessionStorage()
    if (cachedLocationData && isLocationCacheValid(cachedLocationData)) {
        // Return cached data if:
        // 1. Permissions are granted, OR
        // 2. Permissions API is unavailable (fallback for older browsers)
        if (permissionState === "granted" || !permissionsAPIAvailable) {
            return deserializePosition(cachedLocationData.position) ?? undefined
        }
    }

    // Try to get fresh location if permissions allow
    if (permissionState === "granted" || (permissionState === "prompt" && showPrompt) || (!permissionsAPIAvailable && showPrompt)) {
        try {
            const position = await getBrowserLocation(timeoutMs)
            if (position) {
                // Cache the location for 1 minute to avoid redundant requests
                setLocationInSessionStorage({
                    position: serializePosition(position),
                    timestamp: Date.now(),
                })
            }
            return position
        } catch {
            // if we can't get the location, we will return undefined below
            // clear out the cache so we don't return an incorrect location
            clearLocationCache()
        }
    }
    return undefined
}

/**
 * Clear the cached location data
 */
export const clearLocationCache = (): void => {
    try {
        sessionStorage.removeItem(LOCATION_CACHE_KEY)
    } catch {
        console.warn("Failed to clear location cache")
    }
}

/**
 * Will return a date/time string based on today's date and time in the users local timezone
 * Hours will be representing in 24H time
 * Ex: It is 4/10/2025 @ 3:15pm -> "2025-04-10_15-15"
 * @returns string - the current date and time in the format YYYY-MM-DD_HH-MM using 24H time
 */
export const getCurrentDateTimeAsString = (): string => {
    const date = new Date()
    const year = date.getFullYear()
    const month = `${date.getMonth() + 1}`.padStart(2, "0")
    const day = `${date.getDate()}`.padStart(2, "0")
    const hours = `${date.getHours()}`.padStart(2, "0")
    const minutes = `${date.getMinutes()}`.padStart(2, "0")
    return `${year}-${month}-${day}_${hours}-${minutes}`
}

/**
 * Given a URL, fetch the image and return the blob URL
 * @param url The URL of the image to fetch
 * @returns A Promise that resolves to the blob URL of the image or undefined if the fetch fails
 */
export const fetchImage = async (url: string): Promise<string | undefined> => {
    let image: string | undefined
    try {
        const response = await fetch(url)
        if (response.ok) {
            const blob = await response.blob()
            const imageUrl = URL.createObjectURL(blob)
            image = imageUrl
        }
    } catch (error) {
        console.error(`Failed to fetch image for ${url}:`, error)
    }
    return image
}

/**
 * Given a string, return true if it is a valid email address pattern
 * @param email Email to check
 * @returns True if the email pattern is valid, false otherwise
 */
export const isValidEmailString = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return emailRegex.test(email)
}

/**
 * Given a comma-separated string of emails, return an array of emails with any invalid characters removed
 * We expect commas, but also works with semicolons and spaces
 * This does not remove invalid emails, it only cleans up the strings. We validate emails on the backend.
 * @param emailString Comma-separated string of emails
 * @returns Array of emails with any invalid characters removed
 */
export const cleanCommaSeparatedEmails = (emailString: string): string[] => {
    return emailString
        .split(/[,;\s]+/)
        .map(cleanEmailAddress)
        .filter(Boolean)
}

/**
 * Given a string, remove any characters that are not allowed in email addresses
 * @param email Email to clean
 * @returns String with only allowed characters
 */
export const cleanEmailAddress = (email: string): string => {
    return email.trim().replace(/[^a-zA-Z0-9_@.%+-]/g, "")
}

/**
 * Compute the delay (ms) before the next poll attempt using exponential backoff,
 * capped at a maximum. Starts at 250ms so fast operations are detected quickly.
 * Mirrors the Python retry_backoff_seconds formula: min(0.5 * (2 ** attempt), cap).
 *
 * @param attempt - Zero-based poll attempt index.
 * @param maxMs - Maximum delay in milliseconds (default 8000).
 * @returns Milliseconds to wait before the next poll.
 */
export const getTaskPollDelayMs = (attempt: number, maxMs: number = 8000): number => {
    return Math.min(250 * Math.pow(2, attempt), maxMs)
}
